import { useEffect, useState } from 'react';

declare const __BUILD_VERSION__: string;

/** The build this page was loaded from. */
export const RUNNING_VERSION = typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : 'dev';

export interface VersionState {
  running: string;
  /** What the server is serving now, once it has been asked. */
  latest: string | null;
  /** The server has moved on and this page is out of date. */
  stale: boolean;
}

/**
 * Whether the app you are looking at is the app that is deployed.
 *
 * This is a progressive web app, so the browser will happily keep serving the
 * bundle it already has — on an installed tablet app that can be for days.
 * Every change shipped then looks like a change that was never made, which is
 * a miserable way to review anything.
 *
 * So the page asks the server what it is serving. If the answer is a build
 * this page is not, it says so and offers a reload.
 */
export function useAppVersion(pollMinutes = 15): VersionState {
  const [latest, setLatest] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    const check = async (): Promise<void> => {
      try {
        // Straight past the service worker's cache, or this asks the stale
        // copy of the app whether the app is stale.
        const response = await fetch('/api/health', { cache: 'no-store' });
        const body = (await response.json()) as { version?: string };
        if (live && typeof body.version === 'string') setLatest(body.version);
      } catch {
        // Offline is not out of date.
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), pollMinutes * 60_000);
    // Coming back to a tablet that has been asleep for a day is exactly when
    // this matters, and no interval will have fired.
    const onWake = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onWake);

    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [pollMinutes]);

  return {
    running: RUNNING_VERSION,
    latest,
    // 'dev' is the local build, where the two never agree and never should.
    stale: Boolean(latest && latest !== 'dev' && RUNNING_VERSION !== 'dev' && latest !== RUNNING_VERSION),
  };
}

/** Throw away the cached app and load the deployed one. */
export async function reloadToLatest(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.();
    await Promise.all((registrations ?? []).map((r) => r.update()));
    const keys = await caches?.keys?.();
    await Promise.all((keys ?? []).map((key) => caches.delete(key)));
  } catch {
    // Clearing is best-effort; the reload below is the part that matters.
  }
  window.location.reload();
}
